import { useState, useEffect, useRef } from "react";
import axios from "axios";
import Flatpickr from "react-flatpickr";
import moment from "moment";
import Header from "../layout/Header";
import Footer from "../layout/Footer";
import Loader from "../layout/Loader";
import PermissionGate from "../layout/PermissionGate";
import Pagination from "../layout/Pagination";
import { formatDecimal } from "../utils/decimalFormat";
import { truncateDecimal } from "../utils/truncateDecimal";

const DriverReport = () => {
  const [data, setData] = useState([]);
  const [page, setPage] = useState(1);
  const [count, setCount] = useState(0);
  const [search, setSearch] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [start, setStart] = useState(
    moment().subtract(1, "months").startOf("day").format("YYYY-MM-DD"),
  );
  const [end, setEnd] = useState(moment().endOf("day").format("YYYY-MM-DD"));
  const dateRangePickerRef = useRef(null);
  const token = localStorage.getItem("token");
  const limit = process.env.REACT_APP_PAGINATION_LIMIT;

  const fetchData = () => {
    setIsLoading(true);
    axios
      .get(
        `${process.env.REACT_APP_API_ENDPOINT}driver-report?startDate=${start}&endDate=${end}&page=${page}&limit=${limit}&search=${search}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      )
      .then((res) => {
        if (res.data.code === 1) {
          if (page > 1) {
            if (res.data.data.length === 0) {
              setPage(page - 1);
            } else {
              setData(res.data.data);
              setCount(res.data.count);
              setIsLoading(false);
            }
          } else {
            setData(res.data.data);
            setCount(res.data.count);
            setIsLoading(false);
          }
        } else {
          setIsLoading(false);
        }
      })
      .catch(console.error);
  };
  const exportData = async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_ENDPOINT}driver-report-export?startDate=${start}&endDate=${end}&search=${search}`,
        {
          responseType: "blob",
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      const timestamp = Date.now();
      const filename = `driver_report_${timestamp}.xlsx`;
      link.setAttribute("download", filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      console.error("Export failed:", err);
    }
  };
  const openCalendar = () => {
    if (dateRangePickerRef.current) {
      dateRangePickerRef.current.flatpickr.open();
    }
  };
  const reset = () => {
    setPage(1);
    const defaultStart = moment()
      .subtract(1, "months")
      .startOf("day")
      .format("YYYY-MM-DD");
    const defaultEnd = moment().endOf("day").format("YYYY-MM-DD");
    setStart(defaultStart);
    setEnd(defaultEnd);
  };

  useEffect(() => {
    fetchData();
  }, [start, end, page, search]);

  return (
    <div className="d-flex flex-column flex-root">
      {/*begin::Page*/}
      <div className="page d-flex flex-row flex-column-fluid">
        {/*begin::Wrapper*/}
        <div
          className="wrapper d-flex flex-column flex-row-fluid"
          id="kt_wrapper"
        >
          <Header />
          <div className="app-body">
            <div className="toolbar py-3 py-lg-6" id="kt_toolbar">
              {/*begin::Container*/}
              <div
                id="kt_toolbar_container"
                className=" container-xxl  d-flex flex-stack flex-wrap gap-2"
              >
                {/*begin::Page title*/}
                <div className="page-title d-flex flex-column align-items-start me-3 py-2 py-lg-0 gap-2">
                  {/*begin::Title*/}
                  <h1 className="d-flex text-dark fw-bold m-0 fs-3">
                    Driver Report
                  </h1>
                  {/*end::Title*/}
                </div>
                {/*end::Page title*/}
              </div>
              {/*end::Container*/}
            </div>
            {/*end::Toolbar*/}

            {/*begin::Container*/}
            <div
              id="kt_content_container"
              className="d-flex flex-column-fluid align-items-start  container-xxl "
            >
              {/*begin::Post*/}
              <div className="content flex-row-fluid" id="kt_content">
                {/*begin::Card*/}
                <PermissionGate required={`view_driver_report`}>
                  <div className="card">
                    {/*begin::Card header*/}
                    <div className="p-6 overflow-auto">
                      <div className="d-block d-md-flex justify-content-between gap-2">
                        <div className="mb-3">
                          <input
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            type="text"
                            className="form-control w-100"
                            placeholder="Search Driver"
                          />
                        </div>
                        <div className="d-block d-md-flex gap-2 flex-shrink-0">
                          {/* Date Range Picker */}
                          <div className="d-flex position-relative flex-shrink-0 mb-3">
                            <Flatpickr
                              ref={dateRangePickerRef}
                              value={[new Date(start), new Date(end)]}
                              options={{ dateFormat: "d-m-Y", mode: "range" }}
                              onChange={([start, end]) => {
                                if (start && end) {
                                  setStart(moment(start).format("YYYY-MM-DD"));
                                  setEnd(moment(end).format("YYYY-MM-DD"));
                                }
                              }}
                              className="form-control form-control-lg"
                              placeholder="Pick date"
                            />
                            <i
                              onClick={openCalendar}
                              className="bi-duotone bi-calendar fs-5 position-absolute"
                              style={{
                                top: "28%",
                                right: "5%",
                                cursor: "pointer",
                              }}
                            ></i>
                          </div>
                          {/* Reset Button */}
                          <div className="w-100 w-md-auto mb-3">
                            <button
                              onClick={reset}
                              className="btn btn-danger w-100"
                            >
                              <i className="ki-duotone ki-wrench fs-1">
                                <span className="path1"></span>
                                <span className="path2"></span>
                              </i>
                              Reset
                            </button>
                          </div>
                          {/* Export Button */}
                          <div className="w-100 w-md-auto">
                            <button
                              onClick={exportData}
                              className="btn btn-primary w-100"
                            >
                              <i className="ki-duotone ki-cloud-download fs-1">
                                <span className="path1"></span>
                                <span className="path2"></span>
                              </i>
                              Export
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/*end::Card header*/}
                    {/*begin::Card body*/}
                    {isLoading === true ? (
                      <Loader />
                    ) : (
                      <div className="card-body py-4 text-center">
                        {/*begin::Table*/}
                        {data?.length === 0 ? (
                          <div className="text-center w-100">
                            <img
                              className="w-25 mx-auto py-20"
                              src={`${process.env.REACT_APP_PUBLIC_URL}assets/media/misc/no-content.png`}
                              alt="no-content"
                            />
                          </div>
                        ) : (
                          <div className="table-responsive">
                            <table
                              className="table align-middle table-row-dashed fs-6 gy-5"
                              id="kt_table_users"
                            >
                              <thead>
                                <tr className="text-muted fw-bold fs-7 text-uppercase gs-0">
                                  <th className="text-center min-w-100px">
                                    Govt. Id No.
                                  </th>
                                  <th className="text-center min-w-100px">
                                    Driver Name
                                  </th>
                                  <th className="text-center min-w-100px">
                                    Work Phone
                                  </th>
                                  <th className="text-center min-w-100px">
                                    Completed
                                  </th>
                                  <th className="text-center min-w-100px">
                                    Ignored
                                  </th>
                                  <th className="text-center min-w-100px">
                                    Rejected
                                  </th>
                                  <th className="text-center min-w-100px">
                                    Revenue
                                  </th>
                                  <th className="text-center min-w-100px">
                                    Revenue Share
                                  </th>
                                </tr>
                              </thead>
                              <tbody className="text-gray-600 fw-semibold">
                                {data &&
                                  data.map((driver, index) => {
                                    return (
                                      <tr className="text-center" key={index}>
                                        <td>{driver?.govtIdNo ?? "---"}</td>
                                        <td>{driver?.name ?? "---"}</td>
                                        <td>{driver?.workPhone ?? "---"}</td>
                                        <td>{driver?.completed ?? 0}</td>
                                        <td>{driver?.expired ?? 0}</td>
                                        <td>{driver?.rejected ?? 0}</td>
                                        <td>
                                          {formatDecimal(driver?.totalRevenue)}
                                        </td>
                                        <td>
                                          {truncateDecimal(
                                            formatDecimal(driver?.revenueShare),
                                          )}
                                        </td>
                                      </tr>
                                    );
                                  })}
                              </tbody>
                            </table>
                          </div>
                        )}

                        {/*end::Table*/}
                        {/*begin::Paginate*/}
                        {count && Math.ceil(count / limit) > 1 ? (
                          <Pagination
                            page={page}
                            setPage={setPage}
                            count={count}
                            limit={limit}
                          />
                        ) : (
                          ""
                        )}
                        {/*end::Paginate*/}
                      </div>
                    )}
                    {/*end::Card body*/}
                  </div>
                </PermissionGate>
                {/*end::Card*/}
              </div>
              {/*end::Post*/}
            </div>
          </div>
          <Footer />
        </div>
        {/*end::Wrapper*/}
      </div>
      {/*end::Page*/}
    </div>
  );
};

export default DriverReport;
